import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, DownloadIcon, FileTextIcon, ImageIcon, PaperclipIcon, UploadIcon, XIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment, RequestMessage } from '../../data/demandes';
const ease = [0.23, 1, 0.32, 1] as const;
const filledForm: RequestAttachment = {
  name: 'formulaire-reclamation-rempli.pdf',
  type: 'pdf',
  size: '212 Ko'
};
function AttachmentRow({
  file,
  tone
}: {
  file: RequestAttachment;
  tone: 'light' | 'dark';
}) {
  const Icon = file.type === 'image' ? ImageIcon : FileTextIcon;
  return <div className={`mt-3 flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${tone === 'dark' ? 'border-white/20 bg-white/10' : 'border-slate-200 bg-white'}`} data-id="element-1084" data-mp="39nzyta3hwce4">
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${tone === 'dark' ? 'bg-white/15 text-white' : 'bg-ville-mist text-ville-deep'}`} aria-hidden="true" data-id="element-1085" data-mp="1f9z3gz75m7is">
        <Icon className="h-4 w-4" data-id="element-1086" data-mp="1lftp27j113gc" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-1087" data-mp="1gna9x64oy09f">
        <span className={`block truncate text-sm font-bold ${tone === 'dark' ? 'text-white' : 'text-ville-navy'}`} data-id="element-1088" data-mp="1gm91zaiz1tvx">
          {file.name}
        </span>
        <span className={`block text-xs ${tone === 'dark' ? 'text-white/60' : 'text-slate-500'}`} data-id="element-1089" data-mp="17o5dc5cowgzw">{file.size}</span>
      </span>
      <button type="button" aria-label={`Télécharger ${file.name}`} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${tone === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-ville-navy'}`} data-id="element-1090" data-mp="24cuxy3fpbnpk">
        <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1091" data-mp="eygcpt3w6iyt" />
      </button>
    </div>;
}

/** Le fil de messages sert autant aux requêtes qu'aux demandes de permis. */
export function RequestMessages({
  request
}: {
  request: Pick<CitizenRequest, 'messages'>;
}) {
  const [messages, setMessages] = useState<RequestMessage[]>(request.messages);
  const [draft, setDraft] = useState('');
  const [pendingFile, setPendingFile] = useState<RequestAttachment | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const idRef = useRef(0);
  useEffect(() => {
    setMessages(request.messages);
    setDraft('');
    setPendingFile(null);
  }, [request.id, request.messages]);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);
  const awaiting = [...messages].reverse().find(message => message.author === 'ville' && message.awaiting);
  const send = () => {
    const body = draft.trim();
    if (!body && !pendingFile) return;
    idRef.current += 1;
    setMessages(current => [...current, {
      id: `local-${idRef.current}`,
      author: 'citoyen',
      authorName: 'Vous',
      sentAt: 'À l’instant',
      body: body || 'Voici le document demandé.',
      attachment: pendingFile ?? undefined
    }]);
    setDraft('');
    setPendingFile(null);
  };
  return <section aria-labelledby="messages-titre" className="rounded-3xl border border-slate-200" data-id="element-1092" data-mp="316z1ibtjeyw6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4" data-id="element-1093" data-mp="3f6rphzdoj10g">
        <div data-id="element-1094" data-mp="3a7x891vd1kig">
          <h2 id="messages-titre" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-1095" data-mp="3a86y8pgenro5">
            Messages
          </h2>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-1096" data-mp="3jyjguc4ntarn">
            Échangez avec le service {request.service} au sujet de {request.reference}.
          </p>
        </div>
        <span className="rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-1097" data-mp="1t6lr3tnerlj">
          Réponse en 2 jours ouvrables
        </span>
      </div>

      {awaiting && <div className="border-b border-slate-100 bg-ville-sand px-6 py-4" data-id="element-1098" data-mp="1r9itfwvc9963">
          <p className="text-sm font-extrabold text-ville-navy" data-id="element-1099" data-mp="1w83lh90fsmjg">
            {awaiting.awaiting === 'document' ? 'La Ville attend un document de votre part' : 'La Ville attend une précision de votre part'}
          </p>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1100" data-mp="2539sgbbg1ank">
            {awaiting.awaiting === 'document' ? 'Remplissez le formulaire joint au dernier message, puis retournez-le ici.' : 'Répondez au dernier message pour que l’équipe puisse poursuivre le traitement.'}
          </p>
          {awaiting.awaiting === 'document' && <button type="button" onClick={() => setPendingFile(filledForm)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1101" data-mp="1mbqs6c8t0ap8">
              <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1102" data-mp="32vhtyywcsh5b" />
              Joindre le formulaire rempli
            </button>}
        </div>}

      <ol role="log" aria-live="polite" className="space-y-4 px-6 py-6" data-id="element-1103" data-mp="2u3fzricvnx3h">
        {messages.map(message => message.author === 'citoyen' ? <motion.li key={message.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex flex-col items-end" data-id="element-1104" data-mp="2p1gws9l1v272">
              <div className="max-w-[85%] rounded-3xl rounded-br-lg bg-ville-navy px-5 py-3.5 text-[15px] leading-relaxed text-white" data-id="element-1105" data-mp="2b53onr2eod59">
                {message.body}
                {message.attachment && <AttachmentRow file={message.attachment} tone="dark" data-id="element-1106" data-mp="9dgu5fjb2041" />}
              </div>
              <p className="mt-1.5 pr-1 text-xs text-slate-400" data-id="element-1107" data-mp="22uw8q5l2f3do">{message.sentAt}</p>
            </motion.li> : <motion.li key={message.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex flex-col items-start" data-id="element-1108" data-mp="27mmoir2rujch">
              <div className="max-w-[88%] rounded-3xl rounded-bl-lg border border-slate-200 bg-white px-5 py-3.5" data-id="element-1109" data-mp="2ll48cdpk3bp4">
                <p className="text-xs font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-1110" data-mp="2qkz0ekl2i7br">
                  {message.authorName}
                </p>
                <p className="mt-1.5 text-[15px] leading-relaxed text-ville-navy" data-id="element-1111" data-mp="2cnact4zm8m30">{message.body}</p>
                {message.attachment && <AttachmentRow file={message.attachment} tone="light" data-id="element-1112" data-mp="11628ejoyz5yu" />}
                {message.awaiting === 'document' && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-1113" data-mp="165wxqit3jnhs">Document à remplir et à retourner</p>}
                {message.awaiting === 'info' && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-1114" data-mp="tn1e8p026oo5">Réponse attendue</p>}
              </div>
              <p className="mt-1.5 pl-1 text-xs text-slate-400" data-id="element-1115" data-mp="17liyuduo3l1u">{message.sentAt}</p>
            </motion.li>)}
      </ol>

      <div className="border-t border-slate-100 px-4 pb-4 pt-3 sm:px-6" data-id="element-1116" data-mp="exddppzpbu1h">
        <form onSubmit={event => {
        event.preventDefault();
        send();
      }} className="rounded-[24px] border border-slate-200 px-4 pb-3 pt-3.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1117" data-mp="evanb4umzyw8">
          <label htmlFor="message-ville" className="sr-only" data-id="element-1118" data-mp="juvn1hhmsba0">
            Écrire un message au service {request.service}
          </label>
          <textarea id="message-ville" ref={areaRef} rows={1} value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }} placeholder="Écrire un message à la Ville…" className="block max-h-40 w-full resize-none border-0 bg-transparent px-1 text-base leading-relaxed text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1119" data-mp="3nd6s9zah2vfw" />

          {pendingFile && <div className="mt-2 flex items-center gap-2 rounded-full bg-ville-indigo-soft py-1.5 pl-3 pr-1.5 text-xs font-bold text-ville-indigo" data-id="element-1120" data-mp="3pqi1hmrpbozr">
              <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-1121" data-mp="3c8e9gzicn0k" />
              <span className="truncate" data-id="element-1122" data-mp="3bgia5200n9r">{pendingFile.name}</span>
              <button type="button" onClick={() => setPendingFile(null)} aria-label="Retirer le fichier joint" className="grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1123" data-mp="8cclegej05e4">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1124" data-mp="1svgbpb2w6myh" />
              </button>
            </div>}

          <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-1125" data-mp="1iv6363rsy3pn">
            <button type="button" onClick={() => setPendingFile(filledForm)} aria-label="Joindre un fichier" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1126" data-mp="1ixag1b7oid22">
              <PaperclipIcon className="h-5 w-5" aria-hidden="true" data-id="element-1127" data-mp="21s7gqczf9c57" />
            </button>
            <button type="submit" disabled={draft.trim().length === 0 && !pendingFile} aria-label="Envoyer le message" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-[transform,opacity] duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0" data-id="element-1128" data-mp="2zbioy2zj0nv8">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1129" data-mp="1kerh8paza517" />
            </button>
          </div>
        </form>
      </div>
    </section>;
}